import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AdminNav } from "./AdminReports";
import { History, ExternalLink, Undo2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast, Toaster } from "sonner";

const ACTION_COLORS = {
  delete_section: "bg-red-500 text-white",
  edit_section: "bg-cyan-400 text-slate-900",
  edit_episode: "bg-yellow-400 text-slate-900",
  report_resolved: "bg-green-500 text-white",
  report_dismissed: "bg-slate-500 text-white",
  undo_delete_section: "bg-purple-500 text-white",
};

const PAGE_SIZE = 25;

export default function AdminAudit() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState({ logs: [], total: 0, page: 1, total_pages: 1, undo_window_minutes: 30 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/audit?page=${page}&page_size=${PAGE_SIZE}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ logs: [], total: 0, page: 1, total_pages: 1, undo_window_minutes: 30 }))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (authLoading) return <div className="brutal-card p-10 text-center font-mono-display uppercase tracking-widest">Checking the hatch...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const undo = async (auditId, label) => {
    if (!window.confirm(`Restore section "${label}"?`)) return;
    try {
      await api.post(`/admin/audit/${auditId}/undo`);
      toast.success("Section restored.");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Undo failed.");
    }
  };

  return (
    <div data-testid="admin-audit-page">
      <Toaster position="bottom-right" />
      <AdminNav active="audit" />
      <div className="mb-5">
        <div className="font-mono-display text-xs uppercase tracking-[0.3em] text-pink-600 mb-1">Activity</div>
        <h1 className="font-mono-display text-3xl font-bold tracking-tight border-b-4 border-slate-900 pb-2 uppercase">Audit Log</h1>
        <div className="font-mono-display text-xs uppercase tracking-widest text-slate-600 mt-2" data-testid="audit-meta">
          {data.total} total entries · undo window: {data.undo_window_minutes} min
        </div>
      </div>

      {loading ? (
        <div className="brutal-card p-8 text-center font-mono-display uppercase tracking-widest">Loading...</div>
      ) : data.logs.length === 0 ? (
        <div className="brutal-card p-8 text-center" data-testid="audit-empty">
          <History size={28} strokeWidth={2.5} className="mx-auto mb-2 text-slate-400" />
          <div className="font-mono-display uppercase tracking-widest text-slate-700">&gt; No activity yet</div>
        </div>
      ) : (
        <>
          <ul className="space-y-2 font-mono-display text-sm" data-testid="audit-list">
            {data.logs.map((l) => (
              <li key={l.id} className="brutal-card p-3 flex flex-wrap items-start gap-3" data-testid={`audit-${l.id}`}>
                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${ACTION_COLORS[l.action] || "bg-slate-200 text-slate-900"}`}>
                  {l.action.replace(/_/g, " ")}
                </span>
                <span className="font-bold text-slate-900 normal-case font-sans flex-1 min-w-[200px]">{l.target_label || l.target_id}</span>
                <span className="text-slate-600 normal-case font-sans text-xs">{l.actor_email}</span>
                <span className="text-slate-500 normal-case font-sans text-xs">{new Date(l.created_at).toLocaleString()}</span>
                {l.can_undo && (
                  <button onClick={() => undo(l.id, l.target_label)} className="brutal-btn !py-0.5 !px-2 text-xs" style={{ background: "#a855f7", color: "#fff" }} data-testid={`undo-${l.id}`}>
                    <Undo2 size={11} strokeWidth={3} /> Undo
                  </button>
                )}
                {l.target_type === "section" && l.details?.episode_id && (
                  <Link to={`/episodes/${l.details.episode_id}`} className="text-blue-700 normal-case font-sans text-xs flex items-center gap-1">
                    <ExternalLink size={11} strokeWidth={3} /> open
                  </Link>
                )}
                {l.target_type === "episode" && (
                  <Link to={`/episodes/${l.target_id}`} className="text-blue-700 normal-case font-sans text-xs flex items-center gap-1">
                    <ExternalLink size={11} strokeWidth={3} /> open
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {data.total_pages > 1 && (
            <div className="flex items-center justify-between mt-6 gap-3" data-testid="audit-pagination">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="brutal-btn" data-testid="audit-prev">
                <ChevronLeft size={14} strokeWidth={3} /> Prev
              </button>
              <div className="font-mono-display text-sm uppercase tracking-widest">
                Page {data.page} of {data.total_pages}
              </div>
              <button onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))} disabled={page >= data.total_pages} className="brutal-btn" data-testid="audit-next">
                Next <ChevronRight size={14} strokeWidth={3} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
